import React, { useId, useRef } from "react";
import useTranslation from "@hooks/use_translation";

export const CODE_LENGTH = 8;

interface Props {
  hasError: boolean;
  errorId?: string;
  onChange: (value: string) => void;
}

const selectInput = (input: React.MutableRefObject<HTMLInputElement | null>) =>
  input?.current?.select();

// With this small timeout, it will actually highlight any text in the input so you can immediately replace it
const selectAndHighlightInput = (input: React.MutableRefObject<HTMLInputElement | null>) => {
  setTimeout(() => selectInput(input), 0);
};

export const CodeInput = ({ hasError, errorId, onChange }: Props) => {
  const id = useId();
  const { t } = useTranslation("misc");
  const inputRefs = Array.from(new Array(CODE_LENGTH), () => useRef<HTMLInputElement | null>(null));
  const pointerDownIndexRef = useRef<number | null>(null);

  const currentCode = () => inputRefs.map((input) => input.current?.value || "").join("");

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    pointerDownIndexRef.current = null;
    const currentInput = event.currentTarget as HTMLInputElement;
    const previousInputRef = inputRefs[index - 1];
    const nextInputRef = inputRefs[index + 1];

    if (event.key === "Backspace" && index > 0 && !currentInput.value) {
      selectInput(previousInputRef);
    } else if (event.key === "ArrowLeft" && index > 0) {
      selectAndHighlightInput(previousInputRef);
    } else if (event.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      selectAndHighlightInput(nextInputRef);
    }
  };

  const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value.length > 1) {
      fillInputs(index, value);
      return;
    }

    onChange(currentCode());

    if (value && index < CODE_LENGTH - 1) {
      selectInput(inputRefs[index + 1]);
    }
  };

  const fillInputs = (index: number, text: string) => {
    const characters = text.replace(/\s/g, "").split("");
    const startIndex = characters.length >= CODE_LENGTH ? 0 : index;

    characters.forEach((character, i) => {
      const inputIndex = startIndex + i;
      if (inputIndex >= CODE_LENGTH) {
        return;
      }

      inputRefs[inputIndex].current!.value = character;
    });

    onChange(currentCode());

    const lastInputIndex = Math.min(startIndex + characters.length - 1, CODE_LENGTH - 1);
    selectInput(inputRefs[Math.min(lastInputIndex + 1, CODE_LENGTH - 1)]);
  };

  const handlePaste = (startIndex: number, event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const clipboardData = event.clipboardData?.getData("text") || "";
    fillInputs(startIndex, clipboardData);
  };

  const handlePointerDown = (index: number) => {
    pointerDownIndexRef.current = index;
  };

  const handleFocus = (index: number) => {
    const isPointerFocus = pointerDownIndexRef.current === index;
    pointerDownIndexRef.current = null;
    const firstEmptyIndex = inputRefs.findIndex((input) => !input.current?.value);

    if (isPointerFocus && firstEmptyIndex !== -1 && index > firstEmptyIndex) {
      inputRefs[firstEmptyIndex].current?.focus();
    }
  };

  return (
    <fieldset id={id} data-provides="login-security-code">
      <legend className="sr-only">{t("login.securityCode")}</legend>
      <div className="login-security-code__wrapper">
        {inputRefs.map((inputRef, index) => (
          <input
            key={index}
            id={`${id}-${index}`}
            data-test-id={`security-input-${index}`}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            ref={inputRef}
            aria-label={t("login.securityCodeDigit", { position: index + 1, total: CODE_LENGTH })}
            aria-invalid={hasError}
            aria-errormessage={errorId}
            aria-required={true}
            onChange={(e) => handleChange(index, e)}
            onPaste={(e) => handlePaste(index, e)}
            onPointerDown={() => handlePointerDown(index)}
            onFocus={() => handleFocus(index)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            autoFocus={index === 0}
            autoCapitalize="off"
          />
        ))}
      </div>
    </fieldset>
  );
};
